import { type NetworkPing, serverTimeMs } from '@/components/network-status/networkTier'
import { networkKeys } from '@/lib/queryKeys'
import { useQuery } from '@tanstack/react-query'

const INTERNET_PING_URL = 'https://speed.cloudflare.com/__down?bytes=0'

const timeRequest = async (url: string, method: 'GET' | 'HEAD'): Promise<number> => {
	const start = performance.now()
	const response = await fetch(url, { method, cache: 'no-store' })
	await response.arrayBuffer()
	const wallMs = performance.now() - start
	if (!response.ok) {
		throw new Error(`HTTP ${response.status}`)
	}
	// requestStart comes after any handshake, so a fresh connection does not inflate the reading.
	const entry = performance.getEntriesByName(url).at(-1) as PerformanceResourceTiming | undefined
	const ttfb = entry?.responseStart ? entry.responseStart - entry.requestStart : wallMs
	return Math.round(Math.max(0, ttfb - serverTimeMs(response.headers.get('server-timing'))))
}

const fulfilled = (result: PromiseSettledResult<number>) => (result.status === 'fulfilled' ? result.value : undefined)

const measurePing = async (): Promise<NetworkPing> => {
	const [internet, bytebeam] = await Promise.allSettled([
		timeRequest(INTERNET_PING_URL, 'GET'),
		timeRequest(`${window.location.origin}/`, 'HEAD'),
	])
	return { internetMs: fulfilled(internet), bytebeamMs: fulfilled(bytebeam) }
}

/** One tiny request to each host: every 30 s for the footer icon, every 2 s while the dialog is open. */
export const useNetworkPing = ({ live, enabled }: { live: boolean; enabled: boolean }) =>
	useQuery({
		queryKey: networkKeys.ping,
		queryFn: measurePing,
		enabled,
		refetchInterval: live ? 2_000 : 30_000,
		retry: false,
	})
